import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
} from "react";
import classNames from "classnames";
import useClickedOutside from "@src/hooks/useClickedOutside";
import styles from "./AutocompleteInput.module.scss";

export interface Suggestion {
  id: string;
  label: string;
  value: string;
  [key: string]: any;
}

export interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (searchTerm?: string) => void;
  onSuggestionSelect?: (suggestion: Suggestion) => void;
  suggestions?: Suggestion[];
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  autoFocus?: boolean;
  "data-testid"?: string;
}

export default function AutocompleteInput({
  value,
  onChange,
  onSearch,
  onSuggestionSelect,
  suggestions = [],
  placeholder = "Search...",
  className,
  inputClassName,
  inputStyle,
  width,
  height,
  disabled = false,
  required = false,
  label,
  autoFocus = false,
  "data-testid": dataTestId = "autocomplete-input",
}: AutocompleteInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [internalValue, setInternalValue] = useState(value);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useClickedOutside(containerRef as React.RefObject<HTMLElement>, () =>
    setIsOpen(false),
  );

  // Sync internal value with external value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Open/close dropdown as the consumer-supplied suggestions change
  useEffect(() => {
    if (hasUserInteracted) {
      setIsOpen(suggestions.length > 0);
    }
  }, [suggestions, hasUserInteracted]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange(newValue);
      setHighlighted(-1);
      setHasUserInteracted(true);
    },
    [onChange],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Don't interfere with left/right arrow keys - let them work normally
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      return; // Let the browser handle cursor movement
    }

    // Handle Enter key regardless of dropdown state
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && highlighted >= 0 && suggestions.length > 0) {
        submitSearchWithSuggestion(suggestions[highlighted]);
      } else {
        setIsOpen(false);
        setHighlighted(-1);
        onSearch?.(internalValue);
      }
      return;
    }

    // Handle Escape to close dropdown
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
      setHighlighted(-1);
      return;
    }

    // Only handle up/down arrow keys for dropdown navigation when dropdown is open AND has suggestions
    if (isOpen && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((prev) => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const finishSuggestionUi = (suggestion: Suggestion) => {
    // Update the value first
    onChange(suggestion.value);
    setInternalValue(suggestion.value);
    setIsOpen(false);
    setHighlighted(-1);
    setHasUserInteracted(false); // Prevent dropdown from reopening after selection
  };

  const submitSearchWithSuggestion = (suggestion: Suggestion) => {
    finishSuggestionUi(suggestion);
    onSearch?.(suggestion.value);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    finishSuggestionUi(suggestion);
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    } else {
      onSearch?.(suggestion.value);
    }
  };

  const handleFocus = () => {
    setHasUserInteracted(true);
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className={classNames(styles.autocomplete, className)}
      style={{ width: width || "100%" }}
    >
      {label && (
        <label htmlFor="search-input" className={styles.label}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          id="search-input"
          type="text"
          value={internalValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          className={classNames(styles.input, inputClassName)}
          style={{ height, ...inputStyle }}
          data-testid={dataTestId}
        />
        <i
          className={classNames(
            "fa-light fa-magnifying-glass clickable",
            styles.searchIcon,
          )}
          onClick={() => onSearch?.(internalValue)}
          data-testid={`${dataTestId}-search-icon`}
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className={styles.dropdown} data-testid={`${dataTestId}-dropdown`}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              className={classNames(styles.item, {
                [styles.highlighted]: index === highlighted,
              })}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => handleSuggestionClick(suggestion)}
              data-testid={`${dataTestId}-suggestion-${index}`}
            >
              {suggestion.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
